import * as THREE from "three";
import { throttle } from "../utils/perfUtils";
import { Size } from "@react-three/fiber";
import IMouseInputListener from "../inputListeners/MouseInputListener";
import IDefaultClickListener from "../inputListeners/IDefaultClickListener";
import IDefaultHoverListener from "../inputListeners/IDefaultHoverListener";
import { sceneUnitsFromMm } from "../utils/scaler";
import { isEqualWithinTolerance } from "../utils/mathUtils";
import RaycastIntersection from "./RaycastIntersection";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import { vector3ToString } from "../utils/debugUtils";
import { getParentProductMeshUserData } from "../utils/productUserDataUtils";
import ICanvasInputListener from "./ICanvasInputListener";
import TouchInput from "./TouchInput";
import { getSceneObjectDataFromObject3D, isFullyVisible } from "../utils/sceneObjectUtils";
import EditHandleData from "../model/sceneObjectData/EditHandleData";

export default class CanvasInputListener implements ICanvasInputListener {

    mouse : THREE.Vector2 = new THREE.Vector2(0, 0);
    windowSize : Size = { width: 0, height: 0, top: 0, left: 0 };
    mouseInputListeners: IMouseInputListener[] = [];
    defaultClickListeners: IDefaultClickListener[] = [];
    defaultHoverListeners: IDefaultHoverListener[] = [];
    raycaster : THREE.Raycaster = new THREE.Raycaster();
    camera : THREE.Camera | null = null;
    #enabled: boolean = true; // Flag to enable/disable mouse input
    #targetObjects: THREE.Object3D[] = []; // Objects that raycaster hits
    #defaultTargetObjects: THREE.Object3D[] = [];
    #targetPlane: THREE.Plane = new THREE.Plane();
    raycastMode: CanvasInputListenerModeEnum = CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS;
    #scene: THREE.Scene | null = null;
    #targetPlaneHelper: THREE.PlaneHelper;
    #touchInput: TouchInput = new TouchInput();

    private static pointerMoveTolerance: number = 0.001; // Tolerance for mouse movement detection
    private static readonly POINT_SNAP_PIXEL_RADIUS: number = 100; // Desired on-screen snap radius for points, in pixels

    constructor() {
        this.raycaster.params.Line.threshold = sceneUnitsFromMm(1); // Set the threshold for line intersection
        this.raycaster.params.Points.threshold = sceneUnitsFromMm(20); // Initial value, kept up to date relative to camera distance in updatePointThreshold()
        this.raycaster.params.Mesh.threshold = sceneUnitsFromMm(1); // Set the threshold for mesh intersection

        //this.loadFont();

        this.#targetPlaneHelper = new THREE.PlaneHelper(this.#targetPlane, 20, 0x00ff00); // size, color

        this.#touchInput.onPointerMove = this.onTouchPointerMove;
        this.#touchInput.onPointerClick = this.onTouchPointerClick;
        this.#touchInput.onCancel = this.onTouchCancel;
    }

    enable = () => { 
        this.#enabled = true; 
        this.#touchInput.enable();
        //console.info("CanvasInputListener.enable");
    }
    disable = () => { 
        this.#enabled = false; 
        this.#touchInput.disable();
        //console.info("CanvasInputListener.disable");
    }

    onTouchPointerMove = (event: THREE.Vector2) => {
        //console.log("onTouchPointerMove", event);
        const pointerEvent = new PointerEvent('pointermove', {
            clientX: event.x,            
            clientY: event.y,
            bubbles: true,
            cancelable: true
        });
        
        this.handlePointerMove(pointerEvent);

    }

    onTouchPointerClick = (event: THREE.Vector2) => {
        //console.log("onTouchPointerClick", event);

        const mouseEvent = new MouseEvent('click', {
            /* clientX: clientX,
            clientY: clientY, */
            clientX: event.x,            
            clientY: event.y,
            button: 0,
            bubbles: true,
            cancelable: true
        });
        this.handleLeftClick(mouseEvent);
    }

    onTouchCancel = () => {
        if (!this.#enabled) return;
        this.mouseInputListeners.forEach(l => l.cancel());
    }

    getCameraWorldDirection(): THREE.Vector3 {
        if (!this.camera)
            throw new Error("CanvasInputListener has no camera.");

        return this.camera.getWorldDirection(new THREE.Vector3());
    }

    getCameraLayers(): THREE.Layers {
        if (!this.camera)
            throw new Error("CanvasInputListener has no camera.");

        return this.camera.layers;
    }

    setScene(scene: THREE.Scene) {
        this.#scene = scene;

        this.#touchInput.setScene(scene);

        // For debugging target plane orientation
        /* if (!this.#scene.children.includes(this.#targetPlaneHelper))
            this.#scene.add(this.#targetPlaneHelper); */
    }

    getTargetPlane() {
        return this.#targetPlane.clone();
    }

    setTargetPlane(plane: THREE.Plane) {
        this.#targetPlane.copy(plane);
    }

    setTargetPlaneFromCamera(pointInPlane: THREE.Vector3) {

        if (!this.camera)
            throw `CanvasInputListener.setTargetPlaneFromCamera: No camera set`

        const planeNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(this.camera.quaternion);
        const targetPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, pointInPlane);
        this.setTargetPlane(targetPlane);
    }

    setTargetPlaneFromRaycastResult(pointInPlane: THREE.Vector3) {

        if (!this.camera)
            throw `CanvasInputListener.setTargetPlaneFromCamera: No camera set`

        const planeNormal = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
        const targetPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, pointInPlane);
        this.setTargetPlane(targetPlane);
    }

    setRaycastMode(mode: CanvasInputListenerModeEnum) { this.raycastMode = mode; }

    // TODO: Replace with FontProvider
    /* loadFont = () => {
        fetch('/arial.ttf', { method: 'HEAD' })
        .then(res => {
            if (!res.ok) {
                console.log('Font file does not exist');
            }
        })
        .catch(() => console.log('File does not exist'));
    } */

    handlePointerMove = (event: PointerEvent) => {
        if (this.mouseInputListeners.length == 0 && this.defaultHoverListeners.length == 0) 
            return; 

        if (!this.#enabled) {
            //console.log("CanvasInputListener.handleLeftClick: Mouse input is disabled.");
            return; // Ignore left-click if mouse input is disabled
        }

        try
        {
            if (!this.calculateMousePosition(event, this.mouse, CanvasInputListener.pointerMoveTolerance))
                return; // Do not inform listeners if mouse position has not changed

            if (this.setupRaycaster()) {
                const results = this.getRaycasterHits();
                let consumed = false;
                for (let i = this.mouseInputListeners.length - 1; i >= 0; i--) {
                    if (this.mouseInputListeners[i].pointerMove(results[0] ?? undefined)) {
                        consumed = true;
                        break;
                    }
                }

                // Moves the commands did not use trickle down to default listeners (e.g. room note tooltips).
                // A consumed move still clears their hover state.
                this.notifyDefaultHoverListeners(consumed ? [] : results);
            }
        }
        catch (error) {
            if (error instanceof Error) {
                console.error("Error during pointer move handling:", error.message, error.stack);
            } else {
                console.error("Unknown error during pointer move handling:", error);
            }
        }
    }

    // The first listener that consumes the move wins; the rest are told nothing is hovered
    notifyDefaultHoverListeners(raycastResults: RaycastIntersection[]) {
        let consumed = false;
        for (const l of this.defaultHoverListeners) {
            if (consumed) l.defaultPointerMove([]);
            else consumed = l.defaultPointerMove(raycastResults);
        }
    }

    handlePointerLeave = () => {
        this.notifyDefaultHoverListeners([]);
    }

    // Run 10 times per second
    // TODO: throttle only hover check, not the whole move handler
    throttledMove = throttle(this.handlePointerMove, 33);

    setCamera(camera: THREE.Camera) {
        this.camera = camera;
    }

    attachToCanvas(canvas: HTMLCanvasElement) {
        canvas.addEventListener('click', this.handleLeftClick);
        canvas.addEventListener('mouseup', this.handleMouseUp);
        canvas.addEventListener('pointermove', this.throttledMove);
        canvas.addEventListener('pointerleave', this.handlePointerLeave);
        canvas.addEventListener('contextmenu', this.ignoreEvent);
        
        this.#touchInput.attachToCanvas(canvas);
    }

    detachFromCanvas(canvas: HTMLCanvasElement) {
        canvas.removeEventListener('click', this.handleLeftClick); 
        canvas.removeEventListener('mouseup', this.handleMouseUp); 
        canvas.removeEventListener('pointermove', this.throttledMove);
        canvas.removeEventListener('pointerleave', this.handlePointerLeave);
        canvas.removeEventListener('contextmenu', this.ignoreEvent);

        this.#touchInput.detachFromCanvas(canvas);
    }

    ignoreEvent = (event: MouseEvent) => {
        event.preventDefault();
        return false;
    }

    handleMouseUp = (event: MouseEvent) => {
        if (!this.#enabled) return; // Ignore mouse up if mouse input is disabled
        if (event.button === 2) {
            this.handleRightClick(event);
        }
        return true;
    };

    handleRightClick = (event: MouseEvent) => {

        if (!this.#enabled) return; // Ignore right-click if mouse input is disabled

        if (event.button === 2) {
            event.preventDefault(); 
            this.mouseInputListeners.forEach(l => l.rightClick(event, this.raycaster, this.#targetObjects));
        }
    };

    // Calculate mouse position in normalized device coordinates (-1 to +1) for both components
    // Returns true if the mouse position has changed, false otherwise
    calculateMousePosition(event: MouseEvent, targetMousePos: THREE.Vector2, tolerance: number) {
         const x = ((event.clientX - this.windowSize.left) / this.windowSize.width) * 2 - 1;
         const y = -((event.clientY - this.windowSize.top) / this.windowSize.height) * 2 + 1;

         if (isEqualWithinTolerance(x, targetMousePos.x, tolerance) && isEqualWithinTolerance(y, targetMousePos.y, tolerance)) {
             return false; // No change in mouse position
         }
         
        targetMousePos.set(x, y);
        return true;
    }

    setupRaycaster() {
        if (this.camera) {
            this.raycaster.layers = this.camera.layers;
            this.raycaster.setFromCamera(this.mouse, this.camera);
            return true;
        }
        console.error("Camera not set for raycaster.");
        return false;
    }

    // How many world units correspond to one screen pixel at the camera's current distance/zoom
    // from the active target plane. Lets snap tolerances stay constant in screen space rather
    // than in fixed world units, which read very differently when zoomed in vs. out.
    getWorldUnitsPerPixel(): number {
        if (!this.camera)
            return 0;

        return this.#worldUnitsPerPixelAtDistance(Math.abs(this.#targetPlane.distanceToPoint(this.camera.position)));
    }

    // Same measure taken at a specific world point instead of at the target plane, for callers that
    // need it while the raycaster is in RAYCAST_TARGET_OBJECTS mode and the target plane is stale —
    // e.g. keeping a gizmo anchored at that point a constant size on screen.
    getWorldUnitsPerPixelAt(worldPoint: THREE.Vector3): number {
        if (!this.camera)
            return 0;

        return this.#worldUnitsPerPixelAtDistance(this.camera.position.distanceTo(worldPoint));
    }

    #worldUnitsPerPixelAtDistance(distance: number): number {
        if (!this.camera || !this.windowSize.height)
            return 0;

        if (this.camera instanceof THREE.PerspectiveCamera) {
            const visibleHeight = 2 * distance * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
            return visibleHeight / this.windowSize.height;
        } else if (this.camera instanceof THREE.OrthographicCamera) {
            // Orthographic projection has no foreshortening, so the distance plays no part.
            return (this.camera.top - this.camera.bottom) / this.camera.zoom / this.windowSize.height;
        }
        return 0;
    }

    getRaycasterHits(debug: boolean = false) : RaycastIntersection[] {
        if (!this.setupRaycaster())
            return [];

        switch (this.raycastMode) {
            case CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS:
                const results = this.raycaster.intersectObjects(this.#targetObjects, true)
                    .filter(r => isFullyVisible(r.object))
                    // Edit handles draw on top of everything (depthTest off, renderOrder 999), so they
                    // must also win the pick — otherwise geometry that is merely closer to the ray
                    // origin (e.g. another annotation's hit line crossing the handle) steals the click.
                    // sort() is stable, so distance order is kept within each group.
                    .sort((a, b) => Number(this.#isEditHandleHit(b.object)) - Number(this.#isEditHandleHit(a.object)));

                const resultObjects = results.map(r => r.object);
                const debugInfo = resultObjects.map(r =>  { 
                    if (r) {
                        const parentMeshUserData = getParentProductMeshUserData(r);
                        if (parentMeshUserData) {
                            return `${r.type} - ${parentMeshUserData.product.productData.productType}`;
                        }
                        else {
                            return `${r.type} - ${r.name}`;
                        }
                    }
                    return "no object";
                });

                /* if (debug) 
                    console.info(`CanvasInputListener.getRaycasterHits: objects: ${debugInfo}` ); */

                return results.map(r => RaycastIntersection.create(r));
            case CanvasInputListenerModeEnum.RAYCAST_PLANE:
                const intersectPt = this.raycaster.ray.intersectPlane(this.#targetPlane, new THREE.Vector3());
                if (intersectPt) {
                    return [new RaycastIntersection(intersectPt, this.#targetPlane.normal)];
                }
                break;

            default:
                break;
            }
        return [];
    }

    #isEditHandleHit(object: THREE.Object3D): boolean {
        return getSceneObjectDataFromObject3D(object) instanceof EditHandleData;
    }

    /* handleMouseLeftClick = (event: MouseEvent) => {
        
    } */

    handleLeftClick = (event: MouseEvent) => {
        
        if (!this.#enabled) {
            //console.log("CanvasInputListener.handleLeftClick: Mouse input is disabled.");
            return; // Ignore left-click if mouse input is disabled
        }

        try
        {
            this.calculateMousePosition(event, this.mouse, 0.001);

            //console.info(`Mouse pos: ${mouse.current.x}, ${mouse.current.x}`);

            const raycastResults = this.getRaycasterHits(true);
            const topResult = raycastResults.length > 0 ? raycastResults[0] : undefined;
            let consumed = false;
            for (let i = this.mouseInputListeners.length - 1; i >= 0; i--) {
                if (this.mouseInputListeners[i].point(topResult)) {
                    consumed = true;
                    break;
                }
            }

            // Clicks the commands did not use trickle down to default listeners (e.g. room note icons)
            if (!consumed) {
                this.defaultClickListeners.some(l => l.defaultClick(raycastResults));
            }
        }
        catch (ex)
        {
            console.error(`Error in CanvasInputListener.handleLeftClick: ${ex}`);
        }

    }

    addMouseListener(listener: IMouseInputListener) {
        if (!this.mouseInputListeners.includes(listener)) {
            this.mouseInputListeners.push(listener);
            this.outputState();
        }
    }

    removeMouseListener(listener: IMouseInputListener) {
        const index = this.mouseInputListeners.indexOf(listener);
        if (index !== -1) {
            this.mouseInputListeners.splice(index, 1);
            this.outputState();
        }
    }

    addDefaultClickListener(listener: IDefaultClickListener) {
        if (!this.defaultClickListeners.includes(listener)) {
            this.defaultClickListeners.push(listener);
        }
    }

    removeDefaultClickListener(listener: IDefaultClickListener) {
        this.defaultClickListeners = this.defaultClickListeners.filter(l => l !== listener);
    }

    addDefaultHoverListener(listener: IDefaultHoverListener) {
        if (!this.defaultHoverListeners.includes(listener)) {
            this.defaultHoverListeners.push(listener);
        }
    }

    removeDefaultHoverListener(listener: IDefaultHoverListener) {
        listener.defaultPointerMove([]); // Clear any hover state the listener still shows
        this.defaultHoverListeners = this.defaultHoverListeners.filter(l => l !== listener);
    }

    outputState() {
        if (this.mouseInputListeners.length > 0) {
            console.info("Mouse input listeners: ", this.mouseInputListeners.map(l => l.getName()));
        }
        else {  
            console.info("No mouse input listeners registered.");
        }
    }

    setDefaultTargetObjects(targetObjs: THREE.Object3D[]) {
        this.#defaultTargetObjects.length = 0;
        this.#defaultTargetObjects.push(...targetObjs);
        if (this.#targetObjects.length === 0)
            this.resetTargetObjects();
    }

    setTargetObjects(targetObjs: THREE.Object3D[]) {
        this.#targetObjects.length = 0;
        this.#targetObjects.push(...targetObjs);
    }

    resetTargetObjects() {
        this.setTargetObjects(this.#defaultTargetObjects);
    }

    setTouchZoomDollySpeed(speed: number): void {
        this.#touchInput.touchZoomDollySpeed = speed;
    }

    notifyPointerMoveAtCurrentPosition(): void {
        if (!this.#enabled || this.mouseInputListeners.length === 0) return;
        if (this.setupRaycaster()) {
            const results = this.getRaycasterHits();
            for (let i = this.mouseInputListeners.length - 1; i >= 0; i--) {
                if (this.mouseInputListeners[i].pointerMove(results[0] ?? undefined)) break;
            }
        }
    }

}